import { blankEdges } from "./loop";
import {
  CROSSED,
  LINE,
  MAX_SIZE,
  NO_CLUE,
  NONE,
  type Edges,
  type SlitherlinkDefinition,
  type SlitherlinkProgress,
  type SlitherlinkSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Draw a single closed loop along the dotted lines, never crossing or touching itself. A number tells how many of the four sides of its cell the loop uses.",
  fr: "Tracez une seule boucle fermée en suivant les pointillés, sans qu'elle se croise ni se touche. Un nombre indique combien des quatre côtés de sa case la boucle emprunte.",
};

export function emptyDefinition(): SlitherlinkDefinition {
  return {
    puzzleType: "slitherlink",
    typeVersion: 1,
    rows: 5,
    cols: 5,
    clues: Array.from({ length: 5 }, () => NO_CLUE.repeat(5)),
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SIZE, Math.max(1, Math.trunc(value)));
}

function fitLines(source: string[], count: number, length: number, fill: string): string[] {
  return Array.from({ length: count }, (_, i) => {
    const line = source[i] ?? "";
    return line.length >= length ? line.slice(0, length) : line + fill.repeat(length - line.length);
  });
}

/** Crops or pads a clue grid to the given size, keeping the top-left corner. */
export function resizeClues(clues: string[], rows: number, cols: number): string[] {
  return fitLines(clues, rows, cols, NO_CLUE);
}

/** Crops or pads a set of edges to the given grid size, keeping the top-left corner. */
export function resizeEdges(edges: Edges, rows: number, cols: number): Edges {
  return { h: fitLines(edges.h, rows + 1, cols, NONE), v: fitLines(edges.v, rows, cols + 1, NONE) };
}

function sanitize(raw: unknown, allowed: readonly string[]): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((line) =>
    typeof line === "string"
      ? Array.from(line, (char) => (allowed.includes(char) ? char : NONE)).join("")
      : "",
  );
}

/** Reads any `{h, v}` blob as edges of the grid's size, keeping only the allowed marks. */
export function edgesOf(
  raw: unknown,
  rows: number,
  cols: number,
  allowed: readonly string[] = [LINE, NONE],
): Edges {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return blankEdges(rows, cols);
  const { h, v } = raw as { h?: unknown; v?: unknown };
  return resizeEdges({ h: sanitize(h, allowed), v: sanitize(v, allowed) }, rows, cols);
}

export function emptyProgress(definition: SlitherlinkDefinition): SlitherlinkProgress {
  return { version: 1, ...blankEdges(definition.rows, definition.cols) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: SlitherlinkDefinition,
  raw: unknown,
): SlitherlinkProgress {
  return {
    version: 1,
    ...edgesOf(raw, definition.rows, definition.cols, [LINE, CROSSED, NONE]),
  };
}

/** The server only reads segments: crosses are the player's own notes. */
export function serializeSubmission(
  definition: SlitherlinkDefinition,
  progress: SlitherlinkProgress,
): SlitherlinkSubmission {
  const edges = resizeEdges(progress, definition.rows, definition.cols);
  return {
    h: edges.h.map((line) => line.replaceAll(CROSSED, NONE)),
    v: edges.v.map((line) => line.replaceAll(CROSSED, NONE)),
  };
}

export function sameEdges(a: Edges, b: Edges): boolean {
  return a.h.join("\n") === b.h.join("\n") && a.v.join("\n") === b.v.join("\n");
}

export function edgesSignature(edges: Edges): string {
  return `${edges.h.join("\n")}|${edges.v.join("\n")}`;
}
